import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate } from "@/pages/admin/util";
import {
  Loader2, RefreshCw, Plus, Send, Copy, Check, FileText, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { k: "ALL", label: "All" },
  { k: "SENT", label: "Sent" },
  { k: "PAID", label: "Paid" },
  { k: "DRAFT", label: "Draft" },
  { k: "CANCELLED", label: "Cancelled" },
];

const STATUS_STYLES = {
  DRAFT: "bg-[#E2E8F0] text-[#475569]",
  SENT: "bg-[#0F766E]/15 text-[#0F766E]",
  PAID: "bg-[#064E2E] text-white",
  CANCELLED: "bg-[#FCA5A5]/30 text-[#DC2626]",
  OVERDUE: "bg-[#B88A2A]/20 text-[#B88A2A]",
};

const FRONTEND_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function AdminInvoices() {
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === "ALL" ? "/invoices" : `/invoices?status=${tab}`;
      const { data } = await adminApi.get(url);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load invoices");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  async function resend(id) {
    setBusyId(id);
    try {
      await adminApi.post(`/invoices/${id}/resend`);
      toast.success("Invoice resent to customer");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed");
    } finally {
      setBusyId(null);
    }
  }

  async function setStatus(id, status) {
    setBusyId(id);
    try {
      await adminApi.post(`/invoices/${id}/status`, { status });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  }

  function copyPayLink(inv) {
    const url = `${FRONTEND_ORIGIN}/pay/${inv.invoice_number}`;
    navigator.clipboard?.writeText(url);
    toast.success("Pay-link copied");
  }

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Invoicing</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Invoices</h1>
          <p className="text-sm text-[#475569] mt-1">Create, send and track customer invoices. Paid invoices auto-update from Stripe.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="invoices-refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => setOpen(true)} className="btn-primary h-10 py-0 px-4 text-sm" data-testid="invoices-new-btn">
            <Plus size={14} /> New invoice
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm rounded-full transition ${
              tab === t.k
                ? "bg-[#064E2E] text-white"
                : "bg-white border border-[#D7E7DF] text-[#475569] hover:border-[#064E2E]"
            }`}
            data-testid={`invoices-tab-${t.k.toLowerCase()}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="card-default p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.18em]">
            <tr>
              <th className="px-4 py-3">Number</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#475569]"><Loader2 className="inline animate-spin" size={16}/></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center" data-testid="invoices-empty-state">
                <FileText size={28} className="inline text-[#94A3B8] mb-3"/>
                <p className="text-[#071A33] font-medium">No invoices yet</p>
                <p className="text-xs text-[#475569] mt-1">Create a custom invoice for a customer, supplier or one-off charge.</p>
                <button onClick={() => setShowNew(true)} className="btn-primary mt-4 text-sm" data-testid="invoices-empty-cta">
                  <Plus size={14}/> New invoice
                </button>
              </td></tr>
            )}
            {!loading && items.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`}>
                <td className="px-4 py-3 font-medium text-[#071A33]">{inv.invoice_number}</td>
                <td className="px-4 py-3">
                  <div className="text-[#071A33]">{inv.customer_name}</div>
                  <div className="text-xs text-[#94A3B8]">{inv.customer_email}</div>
                </td>
                <td className="px-4 py-3 font-medium text-[#071A33] tabular-nums">£{Number(inv.total).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[inv.status] || "bg-[#F1F5F9] text-[#475569]"}`}>{inv.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#475569]">{fmtDate(inv.created_at, true)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5 flex-wrap">
                    <button onClick={() => copyPayLink(inv)} className="p-2 rounded-full hover:bg-[#F7F4ED]" title="Copy pay-link">
                      <Copy size={14} className="text-[#475569]" />
                    </button>
                    {inv.status !== "PAID" && inv.status !== "CANCELLED" && (
                      <button onClick={() => resend(inv.id)} disabled={busyId === inv.id} className="p-2 rounded-full hover:bg-[#F7F4ED]" title="Resend">
                        <Send size={14} className="text-[#0F766E]" />
                      </button>
                    )}
                    {inv.status === "SENT" && (
                      <button onClick={() => setStatus(inv.id, "PAID")} className="px-3 py-1 rounded-full bg-[#064E2E] text-white text-xs font-medium inline-flex items-center gap-1" data-testid={`mark-paid-${inv.invoice_number}`}>
                        <Check size={12} /> Mark paid
                      </button>
                    )}
                    {inv.status !== "PAID" && inv.status !== "CANCELLED" && (
                      <button onClick={() => setStatus(inv.id, "CANCELLED")} disabled={busyId === inv.id} className="p-2 rounded-full hover:bg-[#FCA5A5]/20" title="Cancel">
                        <Trash2 size={14} className="text-[#DC2626]" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <NewInvoiceDialog onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewInvoiceDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
    vat_rate: 0,
    due_date: "",
    notes: "",
    support_case_id: "",
  });
  const [send, setSend] = useState(true);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateLine = (i, k, v) => {
    const list = [...form.line_items];
    list[i] = { ...list[i], [k]: v };
    update("line_items", list);
  };
  const addLine = () => update("line_items", [...form.line_items, { description: "", quantity: 1, unit_price: 0 }]);
  const removeLine = (i) => update("line_items", form.line_items.filter((_, idx) => idx !== i));

  const subtotal = form.line_items.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const vat = subtotal * Number(form.vat_rate || 0);
  const total = subtotal + vat;

  async function submit(e) {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) {
      toast.error("Customer name + email required");
      return;
    }
    if (total <= 0) {
      toast.error("Invoice total must be greater than zero");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity) || 1,
          unit_price: Number(l.unit_price) || 0,
        })),
      };
      const { data } = await adminApi.post(`/invoices?send=${send}`, payload);
      toast.success(`Invoice ${data.invoice_number} created${send ? " and emailed" : ""}`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create invoice");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4 overflow-y-auto" data-testid="new-invoice-dialog">
      <form onSubmit={submit} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl my-6">
        <div className="px-6 py-5 border-b border-[#E2DCD0] flex items-center gap-3">
          <FileText size={20} className="text-[#064E2E]" />
          <h2 className="font-display text-2xl text-[#071A33]">New invoice</h2>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Customer name</label>
              <input className="field-input" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} required data-testid="invoice-customer-name" />
            </div>
            <div>
              <label className="field-label">Customer email</label>
              <input type="email" className="field-input" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} required data-testid="invoice-customer-email" />
            </div>
            <div>
              <label className="field-label">Phone <span className="text-[#94A3B8] font-normal">(optional)</span></label>
              <input className="field-input" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} />
            </div>
            <div>
              <label className="field-label">Due date <span className="text-[#94A3B8] font-normal">(optional)</span></label>
              <input type="date" className="field-input" value={form.due_date} onChange={(e) => update("due_date", e.target.value)} />
            </div>
          </div>

          <div>
            <label className="field-label">Line items</label>
            <div className="space-y-2.5">
              {form.line_items.map((line, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input className="field-input col-span-6 m-0" placeholder="Description" value={line.description} onChange={(e) => updateLine(i, "description", e.target.value)} required data-testid={`line-desc-${i}`} />
                  <input type="number" min="0" step="0.5" className="field-input col-span-2 m-0 text-right" value={line.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} data-testid={`line-qty-${i}`} />
                  <input type="number" min="0" step="0.01" className="field-input col-span-3 m-0 text-right" placeholder="£ unit price" value={line.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} data-testid={`line-price-${i}`} />
                  <button type="button" onClick={() => removeLine(i)} disabled={form.line_items.length === 1} className="col-span-1 text-[#DC2626] disabled:opacity-30">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addLine} className="text-sm text-[#0F766E] mt-2.5 hover:underline" data-testid="add-line-btn">
              + Add line
            </button>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 pt-2">
            <div>
              <label className="field-label">VAT rate</label>
              <select className="field-input" value={form.vat_rate} onChange={(e) => update("vat_rate", Number(e.target.value))}>
                <option value="0">None (0%)</option>
                <option value="0.05">Reduced (5%)</option>
                <option value="0.2">Standard (20%)</option>
              </select>
            </div>
            <div className="sm:col-span-2 bg-[#F7F4ED] rounded-xl p-4 text-sm">
              <div className="flex justify-between text-[#475569]">
                <span>Subtotal</span><span className="tabular-nums">£{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#475569] mt-1">
                <span>VAT ({(form.vat_rate * 100).toFixed(0)}%)</span><span className="tabular-nums">£{vat.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#071A33] font-semibold text-base mt-2 pt-2 border-t border-[#E2DCD0]">
                <span>Total</span><span className="tabular-nums">£{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="field-label">Notes / Reference <span className="text-[#94A3B8] font-normal">(optional, shown on invoice)</span></label>
            <textarea className="field-input min-h-[80px]" value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} className="accent-[#064E2E]" />
            Email this invoice to the customer now
          </label>
        </div>

        <div className="px-6 py-4 bg-[#F7F4ED] border-t border-[#E2DCD0] flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-outline" disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="invoice-create-btn">
            {busy ? <><Loader2 className="animate-spin" size={16} /> Creating…</> : <>Create invoice</>}
          </button>
        </div>
      </form>
    </div>
  );
}
